import PropTypes from 'prop-types';
import { AlertCircle, Clock } from 'lucide-react';

const FailedAnalysisItem = ({ subcontractor, error, timestamp }) => {
  // Parse error message to extract useful information
  const getErrorType = (errorMsg) => {
    if (errorMsg.includes('PDF parsing')) return 'Document Processing Error';
    if (errorMsg.includes('timeout')) return 'Analysis Timeout';
    if (errorMsg.includes('API')) return 'Service Error';
    return 'Analysis Error';
  };

  const formatTimestamp = (ts) => {
    if (!ts) return null;
    try {
      return new Date(ts).toLocaleString('en-GB', {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    } catch {
      return null;
    }
  };

  return (
    <div className="failed-analysis-item">
      <div className="failure-header">
        <div className="subcontractor-info">
          <AlertCircle className="error-icon" />
          <h4 className="subcontractor-name">{subcontractor}</h4>
        </div>
        {timestamp && (
          <div className="failure-timestamp">
            <Clock className="clock-icon" />
            <span>{formatTimestamp(timestamp)}</span>
          </div>
        )}
      </div>

      <div className="error-details">
        <div className="error-type">{getErrorType(error)}</div>
        <div className="error-message">{error}</div>
      </div>

      <div className="error-suggestions">
        <h5>Possible Solutions:</h5>
        <ul>
          {error.includes('PDF') && (
            <li>Check if the quote document is a valid PDF file</li>
          )}
          {error.includes('timeout') && (
            <li>
              The document may be too large or complex - try a smaller file
            </li>
          )}
          <li>Contact support if the issue persists</li>
        </ul>
      </div>
    </div>
  );
};

FailedAnalysisItem.propTypes = {
  subcontractor: PropTypes.string.isRequired,
  error: PropTypes.string.isRequired,
  timestamp: PropTypes.string,
};

const ScopeFailedAnalysesTab = ({ subcontractorResults }) => {
  // Extract failed analyses
  const failedAnalyses = Object.entries(subcontractorResults)
    .filter(([, result]) => result.error)
    .map(([subcontractor, result]) => ({
      subcontractor,
      error: result.error,
      timestamp: result.timestamp || result.failed_at,
    }));

  if (failedAnalyses.length === 0) {
    return (
      <div className="no-failed-analyses">
        <div className="success-message">
          <div className="success-icon">✓</div>
          <h3>All Analyses Completed Successfully</h3>
          <p>No analysis failures were detected for this scope of works.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="failed-analyses-tab">
      <div className="failed-analyses-header">
        <h3>Analysis Failures ({failedAnalyses.length})</h3>
        <p>
          The following subcontractor analyses encountered errors. Other
          analyses may have completed successfully.
        </p>
      </div>

      <div className="failed-analyses-list">
        {failedAnalyses.map(({ subcontractor, error, timestamp }) => (
          <FailedAnalysisItem
            key={subcontractor}
            subcontractor={subcontractor}
            error={error}
            timestamp={timestamp}
          />
        ))}
      </div>

      <div className="failed-analyses-footer">
        <p>
          <strong>Note:</strong> Partial results may still be available in other
          tabs for subcontractors whose analyses completed successfully.
        </p>
      </div>
    </div>
  );
};

ScopeFailedAnalysesTab.propTypes = {
  subcontractorResults: PropTypes.object.isRequired,
};

export default ScopeFailedAnalysesTab;
